import { useState } from 'react';
import { Copy, Check, ChevronsDown, ChevronsUp } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Props {
  value: string | number | null | undefined;
  maxChars?: number;
  className?: string;
  mono?: boolean;
  /** Quando o valor original é curto o suficiente, renderiza apenas o texto simples. */
}

/**
 * Exibe texto truncado com:
 *  - tooltip nativo no hover (title=full)
 *  - botao expand/collapse quando excede maxChars
 *  - botao copiar (clipboard)
 * Util para paths, hashes, JSON inline em tabelas.
 */
export function Truncated({ value, maxChars = 60, className, mono = false }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  if (value == null || value === '') return <span className="text-muted-foreground">—</span>;
  const s = typeof value === 'string' ? value : String(value);

  const needsTruncate = s.length > maxChars;
  const display = expanded || !needsTruncate ? s : s.slice(0, maxChars - 1) + '…';

  async function copy(e: React.MouseEvent) {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(s);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* clipboard pode falhar em http */ }
  }

  return (
    <span
      className={cn('inline-flex items-baseline gap-1 group', mono && 'font-mono', className)}
      title={s}
    >
      <span className={cn('break-all', expanded ? '' : 'whitespace-nowrap')}>{display}</span>
      {needsTruncate && (
        <button
          type="button"
          onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
          title={expanded ? 'Recolher' : 'Expandir'}
        >
          {expanded ? <ChevronsUp className="h-3 w-3" /> : <ChevronsDown className="h-3 w-3" />}
        </button>
      )}
      <button
        type="button"
        onClick={copy}
        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
        title="Copiar"
      >
        {copied ? <Check className="h-3 w-3 text-success" /> : <Copy className="h-3 w-3" />}
      </button>
    </span>
  );
}
